<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Upload Profile Picture"
    :style="{ width: '500px' }"
  >
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        Upload a profile picture for {{ userName }}. The image will be displayed
        in the app.
      </p>

      <SingleImageUploaderAndDisplay
        :image-data="imageData"
        :organization-id="organizationId"
        label=""
        :disclaimer-text="`Allowed file types: ${allowedImageFileExtensionsByKey}.`"
        :allow-delete-button="false"
        :thumbnail-width="200"
        @upload-complete="handleUploadComplete"
        @upload-failed="handleUploadFailed"
      />
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button label="Close" text @click="handleClose" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import SingleImageUploaderAndDisplay from "@/shared/components/SingleImageUploaderAndDisplay.vue";
import { allowedImageFileExtensionsByKey } from "@/shared/constants/uploader";
import { useToast } from "primevue/usetoast";
import { adminV1OrganizationsProfilesPartialUpdate } from "@/api/admin-profiles/admin-profiles";
import type { Asset } from "@/api/model";
import type AssetFile from "@/shared/datamodels/assetFile";

interface Props {
  visible?: boolean;
  userId: number;
  userName: string;
  organizationId: number;
  currentPicture?: Asset;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
});

const emit = defineEmits<{
  "update:visible": [value: boolean];
  "picture-updated": [];
}>();

const toast = useToast();

const isVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

const imageData = ref<AssetFile | undefined>(undefined);

// Watch for prop changes to update imageData
watch(
  () => props.currentPicture,
  (newPicture) => {
    if (newPicture?.file) {
      imageData.value = newPicture.file as unknown as AssetFile;
    }
  },
  { immediate: true }
);

const handleUploadComplete = async (data: {
  assetId: number;
  asset?: Asset;
}) => {
  try {
    // Update profile with new picture_id
    const result = await adminV1OrganizationsProfilesPartialUpdate(
      props.organizationId,
      props.userId,
      {
        picture_id: data.assetId,
      }
    );

    // Update local image data
    if (result?.picture?.file) {
      imageData.value = result.picture.file as unknown as AssetFile;
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Profile picture updated successfully",
      life: 3000,
    });

    // Emit update and close dialog
    emit("picture-updated");
    isVisible.value = false;
  } catch (error) {
    console.error("Error updating profile picture:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to update profile picture. Please try again.",
      life: 5000,
    });
  }
};

const handleUploadFailed = (data: { error: string }) => {
  console.error("Upload failed:", data.error);
  toast.add({
    severity: "error",
    summary: "Upload Failed",
    detail: data.error || "Failed to upload image. Please try again.",
    life: 5000,
  });
};

const handleClose = () => {
  isVisible.value = false;
};
</script>
